import { useTranslation } from 'react-i18next';

import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';

import type { OrderReturnsSummary, ReturnStatus } from '~/api/Order';

interface ReturnStatusBadgeV3Props {
  summary: OrderReturnsSummary | null | undefined;
  className?: string;
}

type BadgeTone = 'blue' | 'yellow' | 'red' | 'green' | 'neutral';

const STATUS_TONE: Record<ReturnStatus, BadgeTone> = {
  created: 'blue',
  at_pickup_point: 'blue',
  accepted_fully: 'yellow',
  accepted_partially: 'yellow',
  rejected: 'red',
  refunded: 'green',
  finished: 'neutral',
  cancelled: 'neutral',
};

const TONE_CLASSES: Record<BadgeTone, string> = {
  blue: 'bg-system-neutral-1-subtle text-text-primary',
  yellow: 'bg-system-warning-subtle text-system-warning',
  red: 'bg-system-error-subtle text-system-error',
  green: 'bg-system-success-subtle text-system-success',
  neutral: 'bg-surface-transparent-2 text-text-secondary',
};

const ReturnStatusBadgeV3 = ({
  summary,
  className,
}: ReturnStatusBadgeV3Props) => {
  const { t } = useTranslation();

  if (!summary?.latest_status || !summary.is_in_progress) {
    return null;
  }

  const status: ReturnStatus = summary.latest_status;
  const tone = STATUS_TONE[status];

  return (
    <span
      className={cn(
        'inline-flex h-6 items-center rounded-full px-3 py-1',
        TONE_CLASSES[tone],
        className,
      )}
    >
      <Typography.Text200Medium as="span">
        {t(`returnStatus:${status}`)}
      </Typography.Text200Medium>
    </span>
  );
};

export default ReturnStatusBadgeV3;
